import React, { useState, useEffect, useRef, useCallback } from 'react'
import * as DOMPurify from 'dompurify'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { useLongPress } from 'use-long-press'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import TextField from '@mui/material/TextField'
import List from '@mui/material/List'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import SendIcon from '@mui/icons-material/Send'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import GroupIcon from '@mui/icons-material/Group'
import CorrectionIcon from '@mui/icons-material/Spellcheck'
import MuiAvatar from '@mui/material/Avatar'
import TextareaAutosize from '@mui/base/TextareaAutosize'
import { v4 as uuidv4 } from 'uuid'
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useWindowSize } from '../../hooks/useWindowSize'
import {
  useSendMessageMutation,
} from '../../app/services/messages/messagesService'
import {
  selectUser
} from '../auth/authSlice'
import Avatar, { stringToColor } from '../../common/Avatar'
import { getTimestamp, diffMinutes } from '../../common/time'
import { useListLanguagesQuery, INITIAL_LANGUAGES} from '../../app/services/languages/languagesService'
//import { viewportHandler } from '../../index'

const clean = (dirty) => {
  return DOMPurify.sanitize(dirty, { USE_PROFILES: {html: true} })
}

const baseStyle = (isFirst, isLast) => {
  return {
    px: 1.5,
    py: 1,
    borderRadius: 4,
    my: 0.5,
    maxWidth: '80%',
    mt: isFirst ? 2 : 0.2,
    mb: isLast ? 0.5 : 0.2,
    borderBottomRightRadius: isLast ? 16 : 4,
    borderTopRightRadius: isFirst ? 16 : 4,
    whiteSpace: 'pre-line',
  }
}

export const MyMessage = React.memo(({ text, onClick, isFirst, isLast, created_at, correction, showTimestamp, longPressBind }) => {
  const needsCorrection = correction && correction.explanation
  const [showCorrection, setShowCorrection] = useState(false)
  const handleCorrectionClick = () => {
    setShowCorrection(!showCorrection)
  }
  const style = baseStyle(isFirst, isLast)
  const normalStyle = {
    ...style,
    backgroundColor: '#1651a8',
    color: 'white',
  }
  const correctionStyle = {
    ...style,
    backgroundColor: '#f2f2f2',
    color: 'black',
  }
  const correctionIcon = correction && (
    <IconButton style={{width: 40, height: 40, margin: 8, alignSelf: 'start'}} onClick={handleCorrectionClick}>
      <CorrectionIcon color='secondary' />
    </IconButton>
  )
  return (
    <React.Fragment>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
      }}
    >
      {needsCorrection && correctionIcon}
      {showCorrection && needsCorrection && (
        <Box
          sx={correctionStyle}
          dangerouslySetInnerHTML={{ __html: clean(correction.corrected) }}
        />
      )}
      {!showCorrection && (
        <Box
          sx={normalStyle}
          onClick={onClick}
          {...longPressBind()}
        >
          {text}
        </Box>
      )}
    </Box>
    {showCorrection && needsCorrection && (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          component='div'
          sx={{
            color: 'grey.500',
            fontSize: '0.9rem',
            maxWidth: '80%',
            alignSelf: 'flex-end',
          }}
        >
          {correction.explanation}
        </Typography>
      </Box>
    )}
    {isLast && showTimestamp && (
      <Grid container justifyContent='flex-end'>
        <Typography
          component='span'
          sx={{
            color: 'grey.500',
            fontSize: '0.8rem',
          }}
        >
          {getTimestamp(created_at)}
        </Typography>
      </Grid>
    )}
    </React.Fragment>
  )
})

export const OtherMessage = React.memo(({ name, text, onClick, isFirst, isLast, created_at, showTimestamp, longPressBind }) => {
  return (
    <React.Fragment>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
        my: 0.5,
        mb: isLast ? 0.5 : 0.2,
      }}
    >
      <Box
        sx={{
          width: 30,
        }}
      >
        {isLast && (
          <Avatar name={name} 
            sx={{
              height: 30,
              width: 30,
              fontSize: '1rem',
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          marginLeft: 1,
          maxWidth: '80%',
        }}
      >
        {isFirst && (
          <Typography
            component='span'
            sx={{
              fontSize: '0.9rem',
              fontWeight: 600,
              color: stringToColor(name),
              marginLeft: 1.5,
              marginTop: 1.5,
              lineHeight: 1.2,
            }}
          >
            {name}
          </Typography>
        )}
        <Box
          sx={{
            backgroundColor: '#303030',
            px: 1.5,
            py: 1,
            borderRadius: 4,
            borderBottomLeftRadius: isLast ? 16 : 4,
            borderTopLeftRadius: isFirst ? 16 : 4,
            whiteSpace: 'pre-line',
            color: 'white',
          }}
          onClick={onClick}
          {...longPressBind()}
        >
          {text}
        </Box>
      </Box>
    </Box>
    {isLast && showTimestamp && (
      <Grid container justifyContent='flex-start'>
        <Typography
          component='span'
          sx={{
            color: 'grey.500',
            fontSize: '0.8rem',
          }}
        >
          {getTimestamp(created_at)}
        </Typography>
      </Grid>
    )}
    </React.Fragment>
  )
})

export const Message = React.memo(({ id, translations, correction, original_lang_id, sender_id, sender_name, languages, showTimestamp, ...props }) => {
  const user = useSelector(selectUser)
  //const [flipped, setFlipped] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [selectedLang, setSelectedLang] = useState(
    user.default_lang_id
  )
  //console.log('rendering message', id)
  const handleFlip = () => {
    //console.log('handleFlip')
    if (selectedLang === user.default_lang_id) {
      setSelectedLang(user.native_lang_id)
    } else {
      setSelectedLang(user.default_lang_id)
    }
  }
  const handleLongPress = useCallback((e) => {
    //console.log('[handleLongPress]')
    setShowDialog(true)
  }, [])
  const bind = useLongPress(handleLongPress, {
    threshold: 500,
  })
  const handleClose = () => {
    setShowDialog(false)
  }
  const handleItemClick = (lang) => {
    setSelectedLang(lang)
    setShowDialog(false)
  }
  let display_text = translations[selectedLang]
  if (!display_text) {
    setSelectedLang(original_lang_id)
    display_text = translations[original_lang_id]
  }
  return (
    <Box sx={{ userSelect: 'none' }}>
      {user.id === sender_id
        ? (
          <MyMessage
            longPressBind={bind}
            onClick={() => handleFlip()}
            text={display_text}
            showTimestamp={showTimestamp}
            correction={correction}
            {...props}
          />
        ) : (
          <OtherMessage
            longPressBind={bind}
            onClick={() => handleFlip()}
            name={sender_name}
            text={display_text}
            showTimestamp={showTimestamp}
            {...props}
          />
        )
      }
      <Dialog onClose={handleClose} open={showDialog}>
        <List sx={{ pt: 0 }}>
          {languages.map((lang) => {
            return (
              <ListItem disableGutters key={lang.language}>
                <ListItemButton onClick={() => handleItemClick(lang.language)}>
                  <ListItemIcon>
                    {selectedLang === lang.language
                      ? <RadioButtonCheckedIcon />
                      : <RadioButtonUncheckedIcon />
                    }
                  </ListItemIcon>
                  <ListItemText primary={lang.name} />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Dialog>
    </Box>
  )
})

export const MessageInput = ({ room, handleFocusInput }) => {
  const user = useSelector(selectUser)
  const inputRef = useRef()
  const { viewportHandler } = useWindowSize()
  const [sendMessage, { }] = useSendMessageMutation()

  const validationSchema = Yup.object().shape({
    messageInput: Yup.string()
      .required('You cannot send a blank message')
      .max(256, 'Messages cannot exceed 256 characters')
      .test({
        name: 'is-blank',
        skipAbsent: true,
        test(value, ctx) {
          if (value.trim().length === 0) {
            return ctx.createError({ message: 'You cannot send a blank message' })
          }
          return true
        }
      })
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    setFocus,
  } = useForm({
    defaultValues: {
      messageInput: ''
    },
    resolver: yupResolver(validationSchema)
  })

  const submitForm = (data) => {
    const message = data.messageInput.trim()
    if (message.length === 0) {
      return
    }
    sendMessage({
      room_id: room.id,
      type: 'chat',
      created_at: (new Date()).toISOString().replace('Z', '000'),
      id: uuidv4(),
      sender_id: user.id,
      original_lang_id: user.default_lang_id,
      translations: {
        [user.default_lang_id]: message
      }
    })
    setFocus('messageInput')
    reset()
  }
  
  return (
    <Box
      sx={{
        backgroundColor: '#1D2026',
        position: 'absolute',
        zIndex: 1050,
        bottom: '0px !important',
        width: 1,
        py: 1,
        px: 0,
      }}
      component='form'
      noValidate='noValidate'
      onSubmit={handleSubmit(submitForm)}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center'
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            marginLeft: 2
          }}
        >
          <TextField
            id='name'
            name='messageInput'
            placeholder='Send message'
            {...register('messageInput')}
            error={errors.messageInput ? true : false}
            helperText={errors.messageInput?.message}
            onBlur={(e) => viewportHandler(e)}
            onFocus={(e) => handleFocusInput(e)}
            fullWidth
            required
            sx={{
              '& fieldset': {
                border: 'none'
              },
              padding: 0,
            }}
            InputProps={{
              inputComponent: TextareaAutosize,
              inputProps: {
                style: {
                  padding: '10px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  borderRadius: '24px',
                  lineHeight: '1.3rem',
                  border: 'none',
                  outline: 'none',
                  font: 'inherit',
                  color: 'white',
                  background: '#303030',
                  resize: 'none',
                  width: '100%',
                }
              }
            }}
          />
        </Box>
        <Box
          sx={{
            mx: 1,
            padding: 1
          }}
        >
          <IconButton
            type='submit'
            size='large'
            color='white'
            disableRipple
            sx={{
              backgroundColor: '#1651a8',
            }}
          >
            <SendIcon fontSize='small' />
          </IconButton>
        </Box>
      </Box>
    </Box>
  )
}


//          <TextareaAutosize
//            id='name'
//            name='messageInput'
//            placeholder='Send message'
//            {...register('messageInput')}
//            onBlur={(e) => viewportHandler(e)}
//            onFocus={(e) => handleFocusInput(e)}
//            style={{
//              padding: '10px',
//              paddingLeft: '16px',
//              paddingRight: '16px',
//              borderRadius: '24px',
//              lineHeight: '1.3rem',
//              border: 'none',
//              outline: 'none',
//              font: 'inherit',
//              color: 'white',
//              background: '#303030',
//              resize: 'none',
//              width: '100%',
//            }}
//          />

export const messages_skeleton = (
  <List>
  {[...Array(15).keys()].map((i) => {
    const isFirst = (i % 3 === 0) || (i === 0)
    const isLast = (i % 4 === 0) && !isFirst
    return (
      <ListItem key={i}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'flex-end',
          my: 0,
          mb: isLast ? 0.5 : 0,
        }}
      >
        <Box
          sx={{
            width: 30,
          }}
        >
          {isLast && (
            <Skeleton variant='circular' animation='wave'>
              <Avatar icon={<GroupIcon />} name='Any Thing' 
                sx={{
                  height: 30,
                  width: 30,
                }}
              />
            </Skeleton>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            marginLeft: 1,
            maxWidth: '80%',
          }}
        >
          {isFirst && (
            <Skeleton variant='rounded' animation='wave' width="30%">
              <Typography
                component='span'
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  marginLeft: 1.5,
                  marginTop: 1.5,
                  lineHeight: 1.2,
                }}
              >.</Typography>
            </Skeleton>
          )}
          <Box
            sx={{
              px: 1.5,
              py: 0.25,
              borderRadius: 4,
              borderBottomLeftRadius: isLast ? 16 : 4,
              borderTopLeftRadius: isFirst ? 16 : 4,
            }}
          >
            <Skeleton variant='rounded' animation='wave' width="100%">
              <Typography>Lorem ipsum blah blah blah and another long message</Typography>
            </Skeleton>
          </Box>
        </Box>
      </Box>
      </ListItem>
    )
  })}
  </List>
)

export const MessageWindow = React.forwardRef(({ room, messages }, messageWindowRef) => {
  const user = useSelector(selectUser)
  const api = useSelector((state) => state.api)
  const navigate = useNavigate()
  const messagesEndRef = useRef(null);
  const { data: languages = INITIAL_LANGUAGES } = useListLanguagesQuery()
  //console.log('[MessageWindow]', height)

//  useEffect(() => {
//    console.log('[MessageWindow.useEffect] mount')
//    window.addEventListener('resize', viewportHandler);
//    window.addEventListener('scroll', viewportHandler);
//    return () => {
//      console.log('[MessageWindow.useEffect] unmount')
//      window.removeEventListener('resize', viewportHandler);
//      window.removeEventListener('scroll', viewportHandler);
//    }
//  }, [])

//  useEffect(() => {
//    if (messagesEndRef.current) {
//      messagesEndRef.current.scrollIntoView({ behaviour: 'smooth' })
//    }
//  }, [messages, navigate]);

  const scrollToBottom = () => {
    if (messageWindowRef.current) {
      messageWindowRef.current.scrollTop = messageWindowRef.current.scrollHeight
    }
  };

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const sender_names_by_id = room.users.reduce((prev, next) => {
    return {...prev, [next.id]: next.name || next.email}
  }, {})
  //console.log('sender_names_by_id', sender_names_by_id)

  const numMessages = messages.length

  // There seems to be a bug in RTKquery making isLoading not coming correctly
  // This fixes it
  const isLoading = `listMessages("${room.id}")` in api.queries
    ? (messages.length === 0 && api.queries[`listMessages("${room.id}")`].status === "pending")
    : false

  let now = new Date().toISOString().slice(0, 23)
  let lastTimestamp = '2022-01-01T00:00:00.000000'

  const preprocessedMessages = messages.map((message, index) => {
    let isFirst = false
    let isLast = false
    let diffMprev = 0
    let diffMnext = 0
    if (index > 0) {
      diffMprev = diffMinutes(message.created_at, messages[index - 1].created_at)
    }
    if (index < numMessages - 1) {
      diffMnext = diffMinutes(messages[index + 1].created_at, message.created_at)
    }
    if (index === 0) {
      isFirst = true
      if (numMessages > 1) {
        isLast = messages[index + 1].sender_id !== message.sender_id || diffMnext > 15
      }
    }
    if (index === numMessages - 1) {
      isLast = true
      if (numMessages > 1) {
        isFirst = messages[index - 1].sender_id !== message.sender_id || diffMprev > 15
      }
    }
    if (index > 0 && index < numMessages - 1) {
      isFirst = messages[index - 1].sender_id !== message.sender_id || diffMprev > 15
      isLast = messages[index + 1].sender_id !== message.sender_id || diffMnext > 15
    }
    return {
      ...message,
      isFirst: isFirst,
      isLast: isLast,
      showTimestamp: diffMnext > 15 || (index === numMessages - 1 && diffMinutes(now, message.created_at) > 15),
      sender_name: sender_names_by_id[message.sender_id] || 'Unknown User',
      languages: languages.filter((lang) => {
        return lang.language in message.translations
      })
    }
  })

  if (isLoading) {
    return messages_skeleton
  }

  return (
    <Container
      sx={{
        maxHeight: 'calc(100% - 56px - 76px)',
        height: 'calc(100% - 56px - 76px)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'auto',
        overflowX: 'hidden',
        mb: '76px',
        overscrollBehaviorY: 'contain',
      }}
      ref={messageWindowRef}
    >
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          minHeight: '100vh',
        }}
      />
      <List>
        {preprocessedMessages.map((message, index) => {
          return <Message key={message.id} {...message} />
        })}
      </List>
      <Box ref={messagesEndRef} />
    </Container>
  )
})

export default MessageWindow
      
  //<Box sx={{ flexGrow: 1 }} />
